/**
 * mazeStages.ts
 * 迷路アプリのステージ（迷路）定義
 * ステージごとに異なる迷路（グリッド・スタート地点・ゴール地点）を定義する
 */

/** 迷路グリッド上の向き。北から時計回りに45度刻み（0=北、2=東、4=南、6=西） */
export type MazeDirection = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7;

export type MazeCoordinate = { x: number; y: number };

export type MazeStageDefinition = {
  id: number;
  name: string;
  /** 各行を1文字ずつ表現したグリッド。'#'=かべ '.'=通路 'S'=スタート 'G'=ゴール */
  grid: string[];
  start: MazeCoordinate & { dir: MazeDirection };
  goal?: MazeCoordinate;
};

/** 左に45度回転した向きを返す（反時計回り） */
export const turnLeftDirection = (dir: MazeDirection): MazeDirection => ((dir + 7) % 8) as MazeDirection;

/** 右に45度回転した向きを返す（時計回り） */
export const turnRightDirection = (dir: MazeDirection): MazeDirection => ((dir + 1) % 8) as MazeDirection;

/** 向きに応じた移動差分（x, y） */
const DIAGONAL_DELTA = Math.SQRT1_2;
export const DIRECTION_DELTA: Record<MazeDirection, MazeCoordinate> = {
  0: { x: 0, y: -1 },
  1: { x: DIAGONAL_DELTA, y: -DIAGONAL_DELTA },
  2: { x: 1, y: 0 },
  3: { x: DIAGONAL_DELTA, y: DIAGONAL_DELTA },
  4: { x: 0, y: 1 },
  5: { x: -DIAGONAL_DELTA, y: DIAGONAL_DELTA },
  6: { x: -1, y: 0 },
  7: { x: -DIAGONAL_DELTA, y: -DIAGONAL_DELTA },
};

/** グリッド上のセルが通行可能か（範囲内かつ壁でない） */
export const isMazeCellOpen = (stage: MazeStageDefinition, coordinate: MazeCoordinate): boolean => {
  const row = stage.grid[Math.round(coordinate.y)];
  if (row == null) return false;
  const cell = row[Math.round(coordinate.x)];
  return cell === '.' || cell === 'S' || cell === 'G';
};

export const isMazePathOpen = (
  stage: MazeStageDefinition,
  from: MazeCoordinate,
  to: MazeCoordinate,
): boolean => {
  const distance = Math.hypot(to.x - from.x, to.y - from.y);
  const steps = Math.max(1, Math.ceil(distance / 0.1));
  for (let step = 1; step <= steps; step += 1) {
    const progress = step / steps;
    if (!isMazeCellOpen(stage, {
      x: from.x + (to.x - from.x) * progress,
      y: from.y + (to.y - from.y) * progress,
    })) return false;
  }
  return true;
};

const MAZE_CELL_SIZE_MM = 1000;
const MAZE_WALL_HALF_SIZE_MM = 480;

export const measureMazeWallDistanceMm = (
  stage: MazeStageDefinition,
  pose: MazeCoordinate & { dir: MazeDirection },
  maxDistanceMm: number,
): number => {
  const delta = DIRECTION_DELTA[pose.dir];
  const halfSize = MAZE_WALL_HALF_SIZE_MM / MAZE_CELL_SIZE_MM;
  let nearest = Number.POSITIVE_INFINITY;
  stage.grid.forEach((row, y) => [...row].forEach((cell, x) => {
    if (cell !== '#') return;
    const xDistances = delta.x === 0
      ? (pose.x >= x - halfSize && pose.x <= x + halfSize ? [-Infinity, Infinity] : [Infinity, -Infinity])
      : [(x - halfSize - pose.x) / delta.x, (x + halfSize - pose.x) / delta.x].sort((left, right) => left - right);
    const yDistances = delta.y === 0
      ? (pose.y >= y - halfSize && pose.y <= y + halfSize ? [-Infinity, Infinity] : [Infinity, -Infinity])
      : [(y - halfSize - pose.y) / delta.y, (y + halfSize - pose.y) / delta.y].sort((left, right) => left - right);
    const entry = Math.max(xDistances[0], yDistances[0], 0);
    const exit = Math.min(xDistances[1], yDistances[1]);
    if (entry <= exit) nearest = Math.min(nearest, entry * MAZE_CELL_SIZE_MM);
  }));
  return Number.isFinite(nearest) && nearest <= maxDistanceMm ? Math.round(nearest) : 0;
};

const STAGE_1: MazeStageDefinition = {
  id: 1,
  name: 'ステージ1',
  grid: [
    '#######',
    '#.....#',
    '#.S.#.#',
    '#...G.#',
    '#.....#',
    '#######',
  ],
  start: { x: 2, y: 2, dir: 2 },
  goal: { x: 4, y: 3 },
};

const STAGE_2: MazeStageDefinition = {
  id: 2,
  name: 'ステージ2',
  grid: [
    '#########',
    '#S......#',
    '#...#...#',
    '#.......#',
    '#...#...#',
    '#......G#',
    '#########',
  ],
  start: { x: 1, y: 1, dir: 2 },
  goal: { x: 7, y: 5 },
};

const STAGE_3: MazeStageDefinition = {
  id: 3,
  name: 'ステージ3',
  grid: [
    '###########',
    '#S....#...#',
    '#..##.....#',
    '#.....##..#',
    '#..##.....#',
    '#......#.G#',
    '###########',
  ],
  start: { x: 1, y: 1, dir: 2 },
  goal: { x: 9, y: 5 },
};

export const MAZE_STAGES: MazeStageDefinition[] = [STAGE_1, STAGE_2, STAGE_3];

export const getMazeStage = (id: number): MazeStageDefinition =>
  MAZE_STAGES.find((stage) => stage.id === id) ?? MAZE_STAGES[0];
